// src/contexts/ActiveCompanyContext.js
// Resuelve "¿qué empresa está administrando el usuario actualmente?"
//
// Fuentes en orden de prioridad:
//   1. localStorage 'active_company_id' (lo setea el account switcher)
//   2. user.user_type === 'company' (legacy: el auth user ES la empresa)
//   3. null (no hay empresa activa; el usuario sólo está en modo personal)
//
// Expone:
//   - activeCompanyId: UUID o null
//   - companyData: row completa de company_profiles o null
//   - ownedCompanies: lista de empresas que el user administra
//   - switchTo(companyId | null): cambia el contexto + persiste
//   - refreshOwned(): recarga la lista
//   - loading: bool
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import supabase from '../supabase';
import companiesService from '../services/companiesService';

const STORAGE_KEY = 'active_company_id';

const ActiveCompanyContext = createContext({
  activeCompanyId: null,
  companyData: null,
  ownedCompanies: [],
  switchTo: () => {},
  refreshOwned: () => {},
  loading: true,
});

export const ActiveCompanyProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [ownedCompanies, setOwnedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar lista de empresas administradas + resolver default
  const refreshOwned = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setOwnedCompanies([]);
      setActiveCompanyId(null);
      setCompanyData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await companiesService.getMyOwnedCompanies();
    const list = res.ok ? res.companies : [];
    setOwnedCompanies(list);

    // Determinar el activeCompanyId:
    //   prioridad 1: localStorage si todavía está en la lista
    //   prioridad 2: legacy (user.user_type === 'company') → user.id
    //   prioridad 3: null
    let resolved = null;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && list.some(c => c.company_id === stored)) {
      resolved = stored;
    } else if (user?.user_type === 'company' && list.some(c => c.company_id === user.id)) {
      resolved = user.id;
    } else if (user?.user_type === 'company') {
      // Legacy: aunque no esté en la lista (caso edge), usar user.id
      resolved = user.id;
    }
    setActiveCompanyId(resolved);
    setLoading(false);
  }, [isAuthenticated, user?.id, user?.user_type]);

  useEffect(() => { refreshOwned(); }, [refreshOwned]);

  // Cuando cambia activeCompanyId, traer la row completa
  useEffect(() => {
    let mounted = true;
    if (!activeCompanyId) {
      setCompanyData(null);
      return;
    }
    (async () => {
      const res = await companiesService.getCompanyById(activeCompanyId);
      if (mounted && res.ok) setCompanyData(res.company);
    })();
    return () => { mounted = false; };
  }, [activeCompanyId]);

  const switchTo = useCallback((companyId) => {
    if (companyId) {
      localStorage.setItem(STORAGE_KEY, companyId);
      setActiveCompanyId(companyId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      setActiveCompanyId(null);
      setCompanyData(null);
    }
  }, []);

  return (
    <ActiveCompanyContext.Provider value={{
      activeCompanyId,
      companyData,
      ownedCompanies,
      switchTo,
      refreshOwned,
      loading,
    }}>
      {children}
    </ActiveCompanyContext.Provider>
  );
};

export const useActiveCompany = () => useContext(ActiveCompanyContext);
export default ActiveCompanyContext;
